import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarHeart, FileText, HandCoins, IdCard, ReceiptText, ShieldCheck, UsersRound } from 'lucide-react';
import type { HrMeResponse } from '@ip/shared-types';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';

/**
 * Sub-navigation between the three HR surfaces, gated by role:
 * everyone with the hr module sees "My leave"; managers (someone with direct
 * reports) also "Team"; hr/company-admin/superadmin also "HR".
 */
export function HrTabs({ nip, hrMe }: { nip: string; hrMe: HrMeResponse | null }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const tabs = [
    { path: `/${nip}/hr`, label: t('hr.tabs.me'), icon: CalendarHeart, exact: true, visible: true },
    { path: `/${nip}/hr/invoices`, label: t('hr.tabs.invoices'), icon: ReceiptText, exact: false, visible: !!hrMe?.employee?.hasContractor },
    { path: `/${nip}/hr/reimbursements`, label: t('hr.tabs.reimbursements'), icon: HandCoins, exact: false, visible: !!hrMe?.employee || !!hrMe?.isHrAdmin || !!hrMe?.isManager },
    { path: `/${nip}/hr/my-data`, label: t('hr.tabs.myData'), icon: IdCard, exact: false, visible: !!hrMe?.employee },
    { path: `/${nip}/hr/documents`, label: t('hr.tabs.documents'), icon: FileText, exact: false, visible: !!hrMe?.employee },
    { path: `/${nip}/hr/team`, label: t('hr.tabs.team'), icon: UsersRound, exact: false, visible: !!hrMe?.isManager },
    { path: `/${nip}/hr/admin`, label: t('hr.tabs.admin'), icon: ShieldCheck, exact: false, visible: !!hrMe?.isHrAdmin },
  ].filter((tab) => tab.visible);

  if (tabs.length <= 1) return null;

  return (
    <nav aria-label={t('hr.title')} className="bg-muted inline-flex items-center gap-1 self-start rounded-lg p-1">
      {tabs.map((tab) => {
        const active = tab.exact ? pathname === tab.path : pathname.startsWith(tab.path);
        return (
          <button
            key={tab.path}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => navigate(tab.path)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="size-4" />
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}
